/**
 * Hopelapp Dashboard - Dark Theme Colors (Black Mode)
 * Basé sur la charte graphique Hopelapp - Version sombre
 */

import { hopelappDarkPalette } from "assets/theme/hopelapp-palette";

// types
interface ColorsTypes {
  main: string;
  focus: string;
}

interface GradientsTypes {
  main: string;
  state: string;
}

interface SocialMediaColorsTypes {
  main: string;
  dark: string;
}

interface BadgeColorsTypes {
  background: string;
  text: string;
}

interface HopelappBrandTypes {
  main: string;
  light: string;
  dark: string;
  contrastText: string;
}

interface Types {
  background:
    | {
        default: string;
        sidenav?: string;
        card?: string;
        paper?: string;
        hover?: string;
      }
    | any;
  white:
    | {
        main: string;
        focus: string;
      }
    | any;
  text:
    | {
        main: string;
        focus: string;
        primary?: string;
        secondary?: string;
        disabled?: string;
      }
    | any;
  transparent:
    | {
        main: string;
      }
    | any;
  black:
    | {
        light: string;
        main: string;
        focus: string;
      }
    | any;
  primary: ColorsTypes | any;
  secondary: ColorsTypes | any;
  info: ColorsTypes | any;
  success: ColorsTypes | any;
  warning: ColorsTypes | any;
  error: ColorsTypes | any;
  light: ColorsTypes | any;
  dark: ColorsTypes | any;
  grey:
    | {
        [key: string | number]: string;
      }
    | any;
  // Couleurs Hopelapp Brand
  hopelapp: {
    teal: HopelappBrandTypes;
    gold: HopelappBrandTypes;
    mint: HopelappBrandTypes;
    peach: HopelappBrandTypes;
    coral: HopelappBrandTypes;
    darkGreen: HopelappBrandTypes;
  };
  gradients:
    | {
        primary: GradientsTypes;
        secondary: GradientsTypes;
        info: GradientsTypes;
        success: GradientsTypes;
        warning: GradientsTypes;
        error: GradientsTypes;
        light: GradientsTypes;
        dark: GradientsTypes;
        // Gradients Hopelapp
        teal: GradientsTypes;
        gold: GradientsTypes;
        coral: GradientsTypes;
      }
    | any;
  socialMediaColors:
    | {
        facebook: SocialMediaColorsTypes;
        twitter: SocialMediaColorsTypes;
        instagram: SocialMediaColorsTypes;
        linkedin: SocialMediaColorsTypes;
        pinterest: SocialMediaColorsTypes;
        youtube: SocialMediaColorsTypes;
        vimeo: SocialMediaColorsTypes;
        slack: SocialMediaColorsTypes;
        dribbble: SocialMediaColorsTypes;
        github: SocialMediaColorsTypes;
        reddit: SocialMediaColorsTypes;
        tumblr: SocialMediaColorsTypes;
      }
    | any;
  badgeColors:
    | {
        primary: BadgeColorsTypes;
        secondary: BadgeColorsTypes;
        info: BadgeColorsTypes;
        success: BadgeColorsTypes;
        warning: BadgeColorsTypes;
        error: BadgeColorsTypes;
        light: BadgeColorsTypes;
        dark: BadgeColorsTypes;
        // Badges Hopelapp
        teal: BadgeColorsTypes;
        gold: BadgeColorsTypes;
        coral: BadgeColorsTypes;
      }
    | any;
  coloredShadows:
    | {
        [key: string]: string;
      }
    | any;
  inputBorderColor: string;
  tabs:
    | {
        indicator:
          | {
              boxShadow: string;
            }
          | any;
      }
    | any;
  border: {
    light: string;
    main: string;
    dark: string;
  };
}

const colors: Types = {
  // ==========================================
  // BACKGROUNDS - MODE SOMBRE
  // ==========================================
  background: {
    default: hopelappDarkPalette.background.default, // #0d1410 - Noir teinté vert
    paper: hopelappDarkPalette.background.paper, // #141c18
    card: hopelappDarkPalette.background.card, // #1a2420
    sidenav: "#357f84", // Vert Hopelapp - même couleur qu'en mode light
    hover: hopelappDarkPalette.background.hover, // #1f2d28
  },

  // ==========================================
  // TEXTES - MODE SOMBRE
  // ==========================================
  text: {
    main: hopelappDarkPalette.text.secondary,
    focus: hopelappDarkPalette.text.secondary,
    primary: hopelappDarkPalette.text.primary, // #e8f0ec
    secondary: hopelappDarkPalette.text.secondary, // #a8b8ac
    disabled: hopelappDarkPalette.text.disabled,
  },

  transparent: {
    main: "transparent",
  },

  white: {
    main: "#ffffff",
    focus: "#ffffff",
  },

  black: {
    light: "#000000",
    main: "#000000",
    focus: "#000000",
  },

  // ==========================================
  // COULEURS SÉMANTIQUES - MODE SOMBRE
  // ==========================================
  primary: {
    main: hopelappDarkPalette.semantic.primary, // #4a9fa5 - Teal plus clair
    focus: hopelappDarkPalette.brand.teal.light,
  },

  secondary: {
    main: hopelappDarkPalette.semantic.secondary,
    focus: "#9a9eb5",
  },

  info: {
    main: hopelappDarkPalette.semantic.info, // #4a9fa5 - Teal plus clair
    focus: hopelappDarkPalette.brand.teal.light,
  },

  success: {
    main: hopelappDarkPalette.semantic.success,
    focus: "#81c784",
  },

  warning: {
    main: hopelappDarkPalette.semantic.warning, // #eac04d - Gold plus clair
    focus: hopelappDarkPalette.brand.gold.light,
  },

  error: {
    main: "#ef5350", // Rouge adapté au mode sombre
    focus: "#f44336",
  },

  light: {
    main: hopelappDarkPalette.semantic.light, // #2d3d35
    focus: hopelappDarkPalette.brand.mint.light,
  },

  dark: {
    main: hopelappDarkPalette.semantic.dark, // #e8f0ec
    focus: hopelappDarkPalette.brand.darkGreen.dark,
  },

  // ==========================================
  // COULEURS HOPELAPP BRAND - MODE SOMBRE
  // ==========================================
  hopelapp: {
    teal: hopelappDarkPalette.brand.teal,
    gold: hopelappDarkPalette.brand.gold,
    mint: hopelappDarkPalette.brand.mint,
    peach: hopelappDarkPalette.brand.peach,
    coral: hopelappDarkPalette.brand.coral,
    darkGreen: hopelappDarkPalette.brand.darkGreen,
  },

  // ==========================================
  // GRIS - MODE SOMBRE (inversé)
  // ==========================================
  grey: {
    100: "#212529",
    200: "#343a40",
    300: "#495057",
    400: "#6c757d",
    500: "#adb5bd",
    600: "#ced4da",
    700: "#dee2e6",
    800: "#f0f2f5",
    900: "#f8f9fa",
  },

  // ==========================================
  // BORDURES - MODE SOMBRE
  // ==========================================
  border: {
    light: hopelappDarkPalette.border.light,
    main: hopelappDarkPalette.border.main,
    dark: hopelappDarkPalette.border.dark,
  },

  // ==========================================
  // GRADIENTS - MODE SOMBRE
  // ==========================================
  gradients: {
    primary: {
      main: hopelappDarkPalette.brand.teal.main,
      state: hopelappDarkPalette.brand.teal.light,
    },

    secondary: {
      main: "#495361",
      state: "#747b8a",
    },

    info: {
      main: hopelappDarkPalette.brand.teal.main,
      state: hopelappDarkPalette.brand.teal.light,
    },

    success: {
      main: "#43A047",
      state: "#66BB6A",
    },

    warning: {
      main: hopelappDarkPalette.brand.gold.main,
      state: hopelappDarkPalette.brand.gold.light,
    },

    error: {
      main: "#E53935",
      state: "#EF5350",
    },

    light: {
      main: hopelappDarkPalette.brand.mint.dark,
      state: hopelappDarkPalette.brand.mint.main,
    },

    dark: {
      main: hopelappDarkPalette.background.card,
      state: hopelappDarkPalette.background.hover,
    },

    default: {
      main: "#2d3d35",
      state: "#3d5a50",
    },

    // Gradients Hopelapp spécifiques - Mode sombre
    teal: {
      main: hopelappDarkPalette.brand.teal.dark,
      state: hopelappDarkPalette.brand.teal.light,
    },

    gold: {
      main: hopelappDarkPalette.brand.gold.dark,
      state: hopelappDarkPalette.brand.gold.light,
    },

    coral: {
      main: hopelappDarkPalette.brand.coral.dark,
      state: hopelappDarkPalette.brand.coral.light,
    },
  },

  // ==========================================
  // SOCIAL MEDIA
  // ==========================================
  socialMediaColors: {
    facebook: {
      main: "#3b5998",
      dark: "#344e86",
    },

    twitter: {
      main: "#55acee",
      dark: "#3ea1ec",
    },

    instagram: {
      main: "#125688",
      dark: "#0e456d",
    },

    linkedin: {
      main: "#0077b5",
      dark: "#00669c",
    },

    pinterest: {
      main: "#cc2127",
      dark: "#b21d22",
    },

    youtube: {
      main: "#e52d27",
      dark: "#d41f1a",
    },

    vimeo: {
      main: "#1ab7ea",
      dark: "#13a3d2",
    },

    slack: {
      main: "#3aaf85",
      dark: "#329874",
    },

    dribbble: {
      main: "#ea4c89",
      dark: "#e73177",
    },

    github: {
      main: "#f0f0f0",
      dark: "#e0e0e0",
    },

    reddit: {
      main: "#ff4500",
      dark: "#e03d00",
    },

    tumblr: {
      main: "#35465c",
      dark: "#2a3749",
    },
  },

  // ==========================================
  // BADGES - MODE SOMBRE
  // ==========================================
  badgeColors: {
    primary: {
      background: hopelappDarkPalette.brand.mint.dark,
      text: hopelappDarkPalette.brand.teal.light,
    },

    secondary: {
      background: "#3d4555",
      text: "#c5c8d0",
    },

    info: {
      background: hopelappDarkPalette.brand.mint.main,
      text: hopelappDarkPalette.brand.teal.light,
    },

    success: {
      background: "#1b4d1e",
      text: "#81c784",
    },

    warning: {
      background: "#5c4a15",
      text: hopelappDarkPalette.brand.gold.light,
    },

    error: {
      background: "#5c1515",
      text: "#ef9a9a",
    },

    light: {
      background: "#2d3d35",
      text: "#a8b8ac",
    },

    dark: {
      background: hopelappDarkPalette.background.hover,
      text: hopelappDarkPalette.text.primary,
    },

    // Badges Hopelapp - Mode sombre
    teal: {
      background: hopelappDarkPalette.brand.mint.dark,
      text: hopelappDarkPalette.brand.teal.light,
    },

    gold: {
      background: "#5c4a15",
      text: hopelappDarkPalette.brand.gold.light,
    },

    coral: {
      background: "#5c3525",
      text: hopelappDarkPalette.brand.coral.light,
    },
  },

  // ==========================================
  // SHADOWS - MODE SOMBRE
  // ==========================================
  coloredShadows: {
    primary: hopelappDarkPalette.brand.teal.dark,
    secondary: "#0a0a0a",
    info: hopelappDarkPalette.brand.teal.main,
    success: "#2e7d32",
    warning: hopelappDarkPalette.brand.gold.dark,
    error: "#c62828",
    light: "#2d3d35",
    dark: "#000000",
    teal: hopelappDarkPalette.brand.teal.dark,
    gold: hopelappDarkPalette.brand.gold.dark,
    coral: hopelappDarkPalette.brand.coral.dark,
  },

  inputBorderColor: hopelappDarkPalette.border.main,

  tabs: {
    indicator: { boxShadow: hopelappDarkPalette.brand.teal.main },
  },
};

export default colors;
